import { inject } from "@angular/core";
import { ActivatedRouteSnapshot, ResolveFn, Router, RouterStateSnapshot } from "@angular/router";
import { EMPTY, Observable } from "rxjs";
import { PublicEventsService } from "@services";
import {EventFilter, EventFilterParams, EventItem, EventItemType, FindEventsRequest, ListSortOrder} from "@model";
import { EVENT_CALENDAR_ROUTE, FINISHED_EVENTS_ROUTE } from "../app-routing.module";
import {EventCalendarComponent} from "@pages";
import {parseIdListParam, parseLocalDate} from "@utility";

export function createEventCalendarResolver(eventItemType: EventItemType): ResolveFn<Observable<EventItem[]> | EventItem[]> {
  return (route: ActivatedRouteSnapshot, state: RouterStateSnapshot, publicEventsService: PublicEventsService = inject(PublicEventsService), router: Router = inject(Router)): Observable<EventItem[]> | EventItem[] => {
    const [eventFilterParams, paramsValid] = getParams(route, eventItemType);
    if (!paramsValid) {
      router.navigate([eventItemType === EventItemType.FINISHED ? FINISHED_EVENTS_ROUTE : EVENT_CALENDAR_ROUTE]);
      return EMPTY;
    }
    const filter = FindEventsRequest.createEventPageFilter(1,
        eventFilterParams,
        eventItemType === EventItemType.FINISHED ? ListSortOrder.ASC : ListSortOrder.DESC,
        EventCalendarComponent.DEFAULT_PAGE_SIZE);
    return eventItemType === EventItemType.FINISHED ? publicEventsService.filterFinishedEventsList(filter) : publicEventsService.findEventCalendarList(filter);
  };
}

function getParams(route: ActivatedRouteSnapshot, eventItemType: EventItemType): [EventFilterParams, boolean] {
  const eventFilterParams : EventFilterParams = eventItemType === EventItemType.FINISHED ? EventFilterParams.finishedEventsParams() : EventFilterParams.eventCalendarParams();
  let valid = true;

  const name = route.queryParams["name"] || "";
  const dateFrom = route.queryParams["dateFrom"];
  const dateTo = route.queryParams["dateTo"];
  const lengthTo = +route.queryParams["lengthTo"];
  const lengthFrom = +route.queryParams["lengthFrom"];

  type ArrayKeys = Extract<keyof EventFilterParams, 'sports' | 'countries'>;

  const keys: ArrayKeys[] = ['sports', 'countries'];

  keys.forEach((k) => {
    const { ids: paramIds, valid: paramValid } = parseIdListParam(route.queryParams, k);

    if (!paramValid) {
      valid = false;
    } else if (paramIds.length > 0) {
      eventFilterParams[k] = paramIds;
    }
  });

  const lengths: number[] = []
  EventFilter.LENGTH_RANGES.forEach(range => {
    range.to && lengths.push(range.to)
    range.from &&lengths.push(range.from)
  })
  if (lengthTo && lengths.includes(lengthTo)) eventFilterParams.lengthTo = lengthTo;
  else if (lengthTo) valid = false;

  if (lengthFrom && lengths.includes(lengthFrom)) eventFilterParams.lengthFrom = lengthFrom;
  else if (lengthFrom) valid = false;

  const dateFromParam =
      dateFrom && !isNaN(parseLocalDate(dateFrom)?.getTime() ?? NaN)
          ? parseLocalDate(dateFrom)
          : eventFilterParams.dateFrom;

  const dateToParam =
      dateTo && !isNaN(parseLocalDate(dateTo)?.getTime() ?? NaN)
          ? parseLocalDate(dateTo)
          : eventFilterParams.dateTo;


  if (dateFrom && isNaN(new Date(dateFrom).getTime())) valid = false;
  if (dateTo && isNaN(new Date(dateTo).getTime())) valid = false;
  if (eventItemType === EventItemType.FINISHED && dateToParam && eventFilterParams.dateTo && dateToParam > eventFilterParams.dateTo) valid = false;
  if (eventItemType === EventItemType.UPCOMING && dateFromParam && eventFilterParams.dateFrom && dateFromParam < eventFilterParams.dateFrom) valid = false;
  if (dateFromParam && dateToParam && dateFromParam >= dateToParam) valid = false;
  if (valid) {
    eventFilterParams.dateFrom = dateFromParam;
    eventFilterParams.dateTo = dateToParam;
  }

  if (name.trim()) eventFilterParams.name = name;

  return [eventFilterParams, valid];
}

